import React, { useEffect, useState, useMemo } from 'react'
import { useInView, InView } from 'react-intersection-observer'
import { useErrorHandler } from 'react-error-boundary'
import { useScroll, useScrolling } from 'react-use'
import debounce from 'lodash/debounce'

import ListItem from 'app/InspirationPage/components/revamp/ListItem'
import {
  useAPIGetInspirationLastPublishedChecker,
  useAPIGetInspirationList,
} from 'api/inspirations/useAPIInspirations'
import { INSPIRATIONS_LAST_PUBLISHED_DATE_KEY } from 'app/Homepage/constants'
import ListItemLoader from 'app/InspirationPage/components/revamp/ListItemLoader'
import { useGATrackEvent } from 'app/InspirationPage/hooks/useGATrackEvent'
import { useSETrackEvent } from 'app/InspirationPage/hooks/useSETrackEvent'
import { useInspirationsStore } from 'stores/inspirations'
import { useLocalStorage } from 'utils/hooks/useLocalStorage'
import InspirationFeaturedBlock from './InspirationFeaturedBlock'
import { useClickTracker } from 'utils/satu-event-tracker'
import { useTogglesStore } from 'stores/toggles'

import type { FC } from 'react'

interface InspirationBlockProps {
  includeBuktiKarya?: boolean
  categorySlug?: string
  onFirstFetch?: (length: number) => void
}

const InspirationBlock: FC<InspirationBlockProps> = ({
  includeBuktiKarya,
  categorySlug,
  onFirstFetch,
}) => {
  const isIdePraktikV2Enabled = useTogglesStore(
    (state) => state.toggles?.['ide-praktik-v2-enabled']
  )
  const trackClick = useClickTracker({
    enabled: isIdePraktikV2Enabled,
    activityType: 'CLICK_KONTEN_TAB_CERITA_PRAKTIK',
    eventName: 'IDE_PRAKTIK_CORE_PWA_EVENT',
    defaultAttributes: {
      screen_name: 'CERITA_PRAKTIK',
    },
  })
  const scrollRef = React.useRef(null)
  const scrolling = useScrolling(scrollRef)
  const { y: scrollTopY } = useScroll(scrollRef)
  const [inspirationsLastPublishedAt, setInspirationsLastPublishedAt] =
    useLocalStorage(INSPIRATIONS_LAST_PUBLISHED_DATE_KEY, null)
  const {
    // States
    inspirationListScroll,

    // Mutations
    setInspirationMenuHasBadge,
    setInspirationListScroll,
  } = useInspirationsStore()
  const { GATrackEventInspirationsPage } = useGATrackEvent()
  const [loadingRef, loadingIsInView] = useInView()
  const handleError = useErrorHandler()
  const [totalDataBuktiKarya, setTotalDataBuktiKarya] = useState<number | null>(
    null
  )

  const { status: lastPublishCheckerStatus, data: lastPublishCheckerData } =
    useAPIGetInspirationLastPublishedChecker(true)

  const { fetchNextPage, ...inspirationList } = useAPIGetInspirationList({
    categorySlug,
    contentSources: [
      'CS_UNDEFINED',
      'CS_VIDEO_INSPIRASI',
      'CS_PELATIHAN_MANDIRI',
      'CS_CERITA_PRAKTIK',
    ],
  })

  const {
    SETrackCategoryClickIdePraktik,
    SETrackCategoryScrollIdePraktik,
    SETrackExploreClickIdePraktik,
    SETrackExploreScrollIdePraktik,
  } = useSETrackEvent()

  if (inspirationList.isError) {
    handleError(inspirationList.error)
  }

  React.useEffect(() => {
    if (loadingIsInView) {
      fetchNextPage()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loadingIsInView])

  React.useEffect(() => {
    if (inspirationList.isSuccess && lastPublishCheckerStatus === 'success') {
      const publishedAt = lastPublishCheckerData?.data?.[0]?.publishedAt

      const hasNotificationBadge =
        new Date(publishedAt) > new Date(inspirationsLastPublishedAt)

      if (hasNotificationBadge) {
        setInspirationsLastPublishedAt(publishedAt)
        setInspirationMenuHasBadge(false)
      }

      GATrackEventInspirationsPage({
        activity_type: 'OPEN_INSPIRATION_PAGE',
        with_notification: hasNotificationBadge,
      })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [inspirationList.isSuccess, lastPublishCheckerData])

  React.useEffect(() => {
    if (inspirationList.isSuccess) {
      let length = inspirationList?.data?.pages?.[0]?.data?.length

      if (totalDataBuktiKarya !== null) {
        length += totalDataBuktiKarya
      }

      onFirstFetch?.(length)
    }
  }, [
    inspirationList.isSuccess,
    inspirationList?.data,
    onFirstFetch,
    totalDataBuktiKarya,
  ])

  React.useEffect(() => {
    if (inspirationList.isSuccess) {
      const pageLength = inspirationList.data.pages.length
      GATrackEventInspirationsPage({
        activity_type: 'SCROLL_INSPIRATION_PAGE',
        event_trigger: 'INSPIRATION_PAGE',
        content_page: pageLength,
        content_type: inspirationList.data.pages[pageLength - 1].data.map(
          (item) => item.contentType
        ),
        content_id: inspirationList.data.pages[pageLength - 1].data.map(
          (item) => item.contentId
        ),
      })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [inspirationList.data])

  React.useEffect(() => {
    const currentScroll = inspirationListScroll ?? scrollTopY
    scrollRef?.current?.scrollTo?.(0, currentScroll)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  React.useEffect(() => {
    if (!scrolling && scrollTopY > 0) {
      setInspirationListScroll(scrollTopY)
    }
    // eslint-disable-next-line
  }, [scrolling])

  const getPosition = (offset: number, index: number) => offset + index + 1

  const showBuktiKarya = (total: number, position: number) => {
    const MAX = 3

    // If there are more items
    if (total >= MAX && position === MAX) return true

    // If items are less than the MAX
    if (total < MAX && position === total) return true

    return false
  }

  const handleClickItemTracker = (
    contentId: string,
    contentLocation: number
  ) => {
    if (!!categorySlug) {
      SETrackCategoryClickIdePraktik({
        contentId,
        contentLocation,
        pageLocation: categorySlug,
      })
    } else {
      if (!isIdePraktikV2Enabled) {
        SETrackExploreClickIdePraktik({ contentId, contentLocation })
      } else {
        trackClick({
          content_id: contentId,
        })
      }
    }
  }

  /**
   * SCROLL TRACKER
   */
  const [inViewItems, setInViewItems] = useState<
    Array<{
      contentId: string
      contentPage: number
    }>
  >([])

  // This effect will be called in every `inView` events
  useEffect(() => {
    if (inViewItems.length > 0) {
      handleInViewItemTracker(inViewItems)
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [inViewItems])

  // We need to debounce (1 second) the tracker to avoid it being called in every `inView` events.
  const handleInViewItemTracker = useMemo(
    () =>
      debounce((items) => {
        const contentIds = items.map((item) => item.contentId)
        const contentPages = items.map((item) => item.contentPage)

        if (!!categorySlug) {
          SETrackCategoryScrollIdePraktik({
            contentIds,
            contentPages,
            pageLocation: categorySlug,
          })
        } else {
          if (!isIdePraktikV2Enabled) {
            SETrackExploreScrollIdePraktik({
              contentIds,
              contentPages,
            })
          }
        }

        // Remove the previously sent items
        setInViewItems([])
      }, 1000),

    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  )
  // END SCROLL TRACKER

  return (
    <div className="divide-y divide-subdued">
      {inspirationList.isLoading && <ListItemLoader count={3} />}

      {(inspirationList?.data?.pages ?? []).map((page, index) => {
        return (
          <React.Fragment key={index}>
            {page.data.map((item, itemIndex) => {
              const position = getPosition(page.meta.offset, itemIndex)

              return (
                <div
                  key={`list-item-${position}`}
                  data-testid={`list-item-${position}`}
                >
                  <InView
                    threshold={1}
                    triggerOnce
                    onChange={(inView) => {
                      if (inView) {
                        setInViewItems((prevItems) =>
                          prevItems.concat({
                            contentId: item.contentId,
                            contentPage: position,
                          })
                        )
                      }
                    }}
                  >
                    <ListItem
                      data-testid="inspiration-list-item"
                      containerClassName="bg-white"
                      data={item}
                      onClick={() => {
                        GATrackEventInspirationsPage({
                          activity_type: 'CLICK_INSPIRATION_PAGE',
                          event_trigger: 'INSPIRATION_PAGE',
                          content_location: itemIndex + 1,
                          content_type: item.contentType,
                          content_id: item.contentId,
                        })

                        handleClickItemTracker(item.contentId, position)
                      }}
                    />
                  </InView>

                  {includeBuktiKarya &&
                    showBuktiKarya(page.data.length, position) && (
                      <InspirationFeaturedBlock
                        className="border-t border-subdued"
                        categorySlug={categorySlug}
                        onFirstFetch={(length) => {
                          setTotalDataBuktiKarya(length)
                        }}
                      />
                    )}
                </div>
              )
            })}
          </React.Fragment>
        )
      })}

      {inspirationList?.data?.pages[0].data.length === 0 && includeBuktiKarya && (
        <>
          <InspirationFeaturedBlock
            categorySlug={categorySlug}
            onFirstFetch={(length) => {
              setTotalDataBuktiKarya(length)
            }}
          />
        </>
      )}

      {inspirationList.hasNextPage && (
        <div ref={loadingRef}>
          <ListItemLoader />
        </div>
      )}
    </div>
  )
}
export default InspirationBlock
